import React, { useRef } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";
import { Link } from "react-router-dom";
import { Sparkles, Star, ArrowRight, MessageCircle, TrendingUp, Users } from "lucide-react";
import { Button } from "@/components/kit";

const WHATSAPP =
  "https://wa.me/31641262267?text=" +
  encodeURIComponent("Hoi Tim, ik wil graag samenwerken met UPPIT.");

const ease = [0.22, 1, 0.36, 1];

const Hero = () => {
  const sceneRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });
  const tx = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 120, damping: 20 });
  const ty = useSpring(useTransform(my, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 20 });

  const onMove = (e) => {
    const el = sceneRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  const headParent = {
    hidden: {},
    show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
  };
  const line = {
    hidden: { y: "110%" },
    show: { y: "0%", transition: { duration: 1, ease } },
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center overflow-hidden bg-obsidian pt-28 pb-20 lg:pt-24"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 grid-dark opacity-80" />
      <div className="glow-orb w-[720px] h-[720px] bg-electric/25 -top-40 -left-40" />
      <div className="glow-orb w-[560px] h-[560px] bg-electric/20 top-1/3 right-0" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-ivory" />

      <div className="relative w-full max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-6 xl:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease }}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 backdrop-blur-md mb-8"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-electric-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-electric-400" />
              </span>
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/70">
                Online marketingbureau · MKB &amp; ZZP
              </span>
            </motion.div>

            <motion.h1
              variants={headParent}
              initial="hidden"
              animate="show"
              className="font-display font-black text-ivory leading-[0.92] tracking-tight text-[3.25rem] sm:text-6xl lg:text-7xl xl:text-[5.25rem]"
            >
              <span className="reveal-line">
                <motion.span variants={line} className="block">Word online</motion.span>
              </span>
              <span className="reveal-line">
                <motion.span variants={line} className="block text-gradient-blue">
                  onmisbaar.
                </motion.span>
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5, ease }}
              className="mt-7 max-w-xl text-lg lg:text-xl text-white/70 leading-relaxed"
            >
              UPPIT is het AI-gedreven marketingbureau dat ambitieuze
              ondernemers <span className="text-white font-medium">meer leads, hogere conversie
              en betere vindbaarheid</span> geeft — in Google én in ChatGPT.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.65, ease }}
              className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
            >
              <Button to="/ai-scan" variant="primary" testid="hero-cta-aiscan" className="px-8 py-4">
                <Sparkles className="w-4 h-4" />
                Gratis AI Growth Scan
              </Button>
              <Button
                variant="outlineLight"
                href={WHATSAPP}
                target="_blank"
                rel="noopener noreferrer"
                testid="hero-cta-whatsapp"
                className="px-8 py-4"
              >
                <MessageCircle className="w-4 h-4" />
                Plan een gesprek
              </Button>
            </motion.div>

            {/* Trust row */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 0.9 }}
              className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3"
            >
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} className="w-3.5 h-3.5 fill-electric-400 text-electric-400" />
                  ))}
                </div>
                <span className="text-sm text-white/60">
                  <span className="text-white font-semibold">4.8/5</span> op Google
                </span>
              </div>
              <div className="h-4 w-px bg-white/15 hidden sm:block" />
              <div className="flex items-center gap-2 text-sm text-white/60">
                <Users className="w-4 h-4 text-electric-400" />
                <span className="text-white font-semibold">120+</span> ondernemers geholpen
              </div>
              <div className="h-4 w-px bg-white/15 hidden sm:block" />
              <div className="text-sm text-white/60">Reactie binnen <span className="text-white font-semibold">24u</span></div>
            </motion.div>
          </div>

          {/* Right: 3D floating scene */}
          <div className="lg:col-span-6 xl:col-span-7">
            <motion.div
              ref={sceneRef}
              onMouseMove={onMove}
              onMouseLeave={onLeave}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.35, ease }}
              className="scene-3d relative mx-auto w-full max-w-[640px] aspect-[5/4]"
            >
              {/* glow behind */}
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[85%] h-[70%] rounded-full bg-electric/30 blur-[90px]" />

              <motion.div style={{ rotateX: rx, rotateY: ry }} className="preserve-3d absolute inset-0">
                {/* Main dashboard browser */}
                <motion.div
                  style={{ x: tx, y: ty, transform: "translateZ(60px)" }}
                  className="absolute left-[4%] top-[15%] w-[78%] preserve-3d"
                >
                  <div className="animate-float-slow rounded-2xl overflow-hidden border border-white/15 bg-obsidian-800 shadow-float">
                    <div className="flex items-center gap-1.5 px-4 py-2.5 bg-obsidian-700 border-b border-white/10">
                      <span className="w-2.5 h-2.5 rounded-full bg-signal/80" />
                      <span className="w-2.5 h-2.5 rounded-full bg-electric-300/80" />
                      <span className="w-2.5 h-2.5 rounded-full bg-white/25" />
                      <span className="ml-3 text-[10px] font-mono text-white/40 truncate">uppit.nl/dashboard</span>
                    </div>
                    <img
                      src="/v2/dashboard-hero.jpg"
                      alt="UPPIT marketing dashboard met groeicijfers"
                      className="w-full block"
                      loading="eager"
                    />
                  </div>
                </motion.div>

                {/* Floating phone */}
                <motion.div
                  style={{ x: tx, y: ty, transform: "translateZ(120px)" }}
                  className="absolute right-[1%] bottom-[2%] w-[30%]"
                >
                  <div className="animate-float-med rounded-[1.6rem] overflow-hidden border-4 border-obsidian-600 shadow-float bg-obsidian-800">
                    <img
                      src="/v2/mobile-ui.jpg"
                      alt="UPPIT campagne-app op mobiel"
                      className="w-full block"
                      loading="lazy"
                    />
                  </div>
                </motion.div>

                {/* Stat card: ROAS */}
                <motion.div
                  style={{ x: tx, y: ty, transform: "translateZ(150px)" }}
                  className="absolute -left-3 sm:left-[-6%] top-[-4%] w-[46%] max-w-[220px]"
                >
                  <div className="animate-float-med glass-dark rounded-2xl p-4 shadow-float">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">ROAS</span>
                      <TrendingUp className="w-3.5 h-3.5 text-electric-300" />
                    </div>
                    <div className="font-display font-black text-2xl text-white leading-none">+312%</div>
                    <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full w-[82%] rounded-full bg-gradient-to-r from-electric to-electric-300" />
                    </div>
                  </div>
                </motion.div>

                {/* Stat card: leads */}
                <motion.div
                  style={{ x: tx, y: ty, transform: "translateZ(110px)" }}
                  className="absolute right-[4%] top-[-3%] w-[42%] max-w-[210px]"
                >
                  <div className="animate-float-slow glass-dark rounded-2xl p-4 shadow-float">
                    <div className="flex items-center gap-2">
                      <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-electric-400 opacity-75" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-electric-400" />
                      </span>
                      <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">Live</span>
                    </div>
                    <div className="mt-1.5 font-display font-black text-2xl text-white leading-none">24 leads</div>
                    <div className="text-[11px] text-white/50 mt-0.5">vandaag binnengekomen</div>
                  </div>
                </motion.div>

                {/* Founder card */}
                <motion.div
                  style={{ x: tx, y: ty, transform: "translateZ(140px)" }}
                  className="absolute left-[-2%] sm:left-[-5%] bottom-[4%] max-w-[250px]"
                >
                  <div data-testid="hero-founder-card" className="animate-float-med glass-dark rounded-2xl p-3 pr-5 shadow-float flex items-center gap-3">
                    <img
                      src="/team/tim-avatar.webp"
                      alt="Tim Roosendaal, oprichter van UPPIT"
                      loading="lazy"
                      className="w-12 h-12 rounded-full object-cover border-2 border-electric/60 shrink-0"
                    />
                    <div>
                      <div className="font-display font-bold text-sm text-white leading-tight">Tim Roosendaal</div>
                      <div className="text-[11px] text-white/55">Je directe aanspreekpunt</div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
